/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled } from '@mui/material/styles';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { FaEye, FaBan } from "react-icons/fa";
import { FiMaximize2, FiX } from "react-icons/fi";
import defaultCarIcon from "assets/default_car_icon.png";
import notFoundImg from "assets/not_found.png";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiAvatar from "components/VuiAvatar";
import VuiBadge from "components/VuiBadge";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import ClientDetailsModal from "./components/ClientDetailsModal";
import ClientEditModal from "./components/ClientEditModal";

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// Create default icon instance
const defaultIcon = L.divIcon({
  className: 'car-marker-icon',
  html: `<img 
    src="${defaultCarIcon}" 
    style="
      width: auto;
      height: 40px;
      object-fit: contain;
    "
    alt="car"
  />`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20]
});

// Create a custom icon class for car markers
const createCarIcon = (iconUrl) => {
  if (!iconUrl) return defaultIcon;
  
  try {
    return L.divIcon({
      className: 'car-marker-icon',
      html: `<img 
        src="${iconUrl}" 
        style="
          width: auto;
          height: 40px;
          object-fit: contain;
        "
        alt="car"
      />`,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
      popupAnchor: [0, -20]
    });
  } catch (error) {
    console.error('Error creating car icon:', error);
    return defaultIcon;
  }
};

// Add this CSS to your component's style section
const carMarkerStyles = `
  .car-marker-icon {
    background: none;
    border: none;
  }
  .car-marker-icon img {
    width: auto !important;
    height: 40px !important;
    object-fit: contain;
  }
`;

function Client() {
  const { t, i18n } = useTranslation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [clientDetails, setClientDetails] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClientForEdit, setSelectedClientForEdit] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [highlightedClient, setHighlightedClient] = useState(null);
  const mapRef = useRef(null);
  const fullscreenMapRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.csc-taxi.tech';

  // Helper function to calculate age from birth date
  const calculateAge = (birthDate) => {
    if (!birthDate) return 'N/A';
    const today = new Date();
    const birthDateObj = new Date(birthDate);
    let age = today.getFullYear() - birthDateObj.getFullYear();
    const monthDiff = today.getMonth() - birthDateObj.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDateObj.getDate())) {
      age--;
    }
    return age;
  };

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line
  }, [search]);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sign-in';
        return;
      }
      // Build query params
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const url = `${API_URL}/api/admin/client?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language,
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        window.location.href = '/sign-in';
        return;
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new TypeError("Oops, we haven't got JSON!");
      }
      const result = await response.json();
      if (result.status === 1) {
        setClients(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch clients');
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
      if (error.message.includes('token') || error.message.includes('Session expired')) {
        window.location.href = '/sign-in';
      }
    } finally {
      setLoading(false);
    }
  };

  const openMenu = (event, client) => {
    setMenu(event.currentTarget);
    setSelectedClient(client);
  };

  const closeMenu = () => {
    setMenu(null);
    setSelectedClient(null);
  };

  const handleEdit = (client) => {
    setSelectedClientForEdit(client);
    setShowEditModal(true);
    closeMenu();
  };

  const handleDelete = (client) => {
    setClientToDelete(client);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sign-in';
        return;
      }

      const response = await fetch(`${API_URL}/api/admin/client/${clientToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        window.location.href = '/sign-in';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      await fetchClients();
      setDeleteConfirmOpen(false);
      setClientToDelete(null);
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setClientToDelete(null);
  };

  const handleShowDetails = async (client) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sign-in';
        return;
      }

      const response = await fetch(`${API_URL}/api/admin/client/${client.id}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        window.location.href = '/sign-in';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 1) {
        setClientDetails(result.data);
        setShowModal(true);
      } else {
        throw new Error(result.message || 'Failed to fetch client details');
      }
    } catch (error) {
      console.error('Error fetching client details:', error);
    }
  };

  const handleSaveClient = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sign-in';
        return;
      }

      const url = selectedClientForEdit
        ? `${API_URL}/api/admin/client/${selectedClientForEdit.id}`
        : `${API_URL}/api/admin/client`;

      const response = await fetch(url, {
        method: selectedClientForEdit ? 'PUT' : 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        window.location.href = '/sign-in';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      await fetchClients();
      setShowEditModal(false);
      setSelectedClientForEdit(null);
    } catch (error) {
      console.error('Error saving client:', error);
    }
  };

  const handleClientSelect = (clientId) => {
    setSelectedClients(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(clientId)) {
        newSelected.delete(clientId);
      } else {
        newSelected.add(clientId);
      }
      return newSelected;
    });

    // Find the selected client
    const client = clients.find(c => c.id === clientId);
    if (client && client.current_latitude && client.current_longitude) {
      const position = [client.current_latitude, client.current_longitude];
      // Zoom to client location in both maps
      if (mapRef.current) {
        mapRef.current.setView(position, 15);
      }
      if (fullscreenMapRef.current) {
        fullscreenMapRef.current.setView(position, 15);
      }
    }
  };

  const clearSelection = () => {
    setSelectedClients(new Set());
  };

  const handleMarkerClick = (clientId) => {
    setHighlightedClient(clientId);
    // Find the row element and scroll it into view
    const rowElement = document.getElementById(`client-row-${clientId}`);
    if (rowElement) {
      rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // Clear highlight after 2 seconds
    setTimeout(() => setHighlightedClient(null), 2000);
  };

  const handleBan = async (client) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sign-in';
        return;
      }

      const response = await fetch(`${API_URL}/api/admin/client/ban/${client.id}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        window.location.href = '/sign-in';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      await fetchClients();
    } catch (error) {
      console.error('Error banning client:', error);
    }
  };

  const handleUnban = async (client) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sign-in';
        return;
      }

      const response = await fetch(`${API_URL}/api/admin/client/unban/${client.id}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        window.location.href = '/sign-in';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      await fetchClients(); // Refresh list after unban
    } catch (error) {
      console.error('Error unbanning client:', error);
    }
  };

  const columns = [
    { name: "client", align: "left", label: t('client_table.client') },
    { name: "rating", align: "center", label: t('client_table.rating') },
    { name: "gender", align: "center", label: t('client_table.gender') },
    { name: "age", align: "center", label: t('client_table.age') },
    { name: "action", align: "center", label: t('client_table.action') },
  ];

  const rows = clients.map((client) => ({
    client: (
      <VuiBox
        id={`client-row-${client.id}`}
        display="flex"
        alignItems="center"
        px={1}
        py={0.5}
        onClick={() => handleClientSelect(client.id)}
        sx={{
          cursor: 'pointer',
          background: selectedClients.has(client.id) 
            ? 'rgba(33, 150, 243, 0.15)' 
            : highlightedClient === client.id 
              ? 'rgba(255, 193, 7, 0.15)' 
              : 'transparent',
          borderRadius: '8px',
          transition: 'background 0.2s',
          '&:hover': {
            background: selectedClients.has(client.id) 
              ? 'rgba(33, 150, 243, 0.25)' 
              : highlightedClient === client.id 
                ? 'rgba(255, 193, 7, 0.25)' 
                : 'rgba(255, 255, 255, 0.05)',
          },
        }}
      >
        <VuiBox mr={2}>
          <VuiAvatar
            src={client.profile_photo || notFoundImg}
            alt={`${client.first_name} ${client.last_name}`}
            size="sm"
            variant="rounded"
          />
        </VuiBox>
        <VuiBox display="flex" flexDirection="column">
          <VuiTypography variant="button" color="white" fontWeight="medium">
            {`${client.first_name} ${client.last_name}`}
            {(client.banned_at || client.banned_by || client.banning_reason) && (
              <FaBan style={{ color: "#4CAF50", marginLeft: '8px' }} title={t('client_table.banned')} />
            )}
          </VuiTypography>
          <VuiTypography variant="caption" color="text" style={{direction:"ltr"}}>
            {client.phone || t('common.na')}
          </VuiTypography>
        </VuiBox>
      </VuiBox>
    ),
    rating: (
      <VuiBox display="flex" flexDirection="column" alignItems="center">
        <VuiTypography variant="button" color="white" fontWeight="medium">
          {(client.rating || 0).toFixed(1)} ⭐
        </VuiTypography>
        <VuiTypography variant="caption" color="text">
          ({client.rating_count || 0} {t('client_table.reviews')})
        </VuiTypography>
      </VuiBox>
    ),
    gender: (
      <VuiTypography variant="button" color="white" fontWeight="medium">
        {client.gender ? t(`client_table.${client.gender.toLowerCase()}`) : t('common.na')}
      </VuiTypography>
    ),
    age: (
      <VuiTypography variant="button" color="white" fontWeight="medium">
        {calculateAge(client.birth_date)}
      </VuiTypography>
    ),
    action: (
      <VuiBox display="flex" flexDirection="row" gap={1}>
        <FaEye
          style={{ color: "#1976d2", cursor: "pointer" }}
          title={t('client_table.show_details')}
          onClick={() => handleShowDetails(client)}
        />
        {client.banned_at || client.banned_by || client.banning_reason ? (
          <FaBan
            style={{ color: "#4CAF50", cursor: "pointer" }}
            title={t('client_table.unban_client')}
            onClick={() => handleUnban(client)}
          />
        ) : (
          <FaBan
            style={{ color: "#e53935", cursor: "pointer" }}
            title={t('client_table.ban_client')}
            onClick={() => handleBan(client)}
          />
        )}
      </VuiBox>
    ),
  }));

  return (
    <DashboardLayout>
      <style>{carMarkerStyles}</style>
      <DashboardNavbar />
      <VuiBox py={3} minHeight="100vh" display="flex" flexDirection="column" flex={1}>
        <VuiBox mb={3} flex={1} display="flex" flexDirection={{ xs: 'column', lg: 'row' }} gap={3}>
          {/* Client Table Card */}
          <Card sx={{ flex: 2, minWidth: 0, overflow: 'hidden', maxHeight: '458px' }}>
            {/* Search and filter controls */}
            <VuiBox display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ sm: 'center' }} mb={2} px={3}>
              <TextField
                variant="outlined"
                size="small"
                placeholder={t('client_table.search_placeholder')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  style: { background: '#22223a', color: '#fff', borderRadius: 8 },
                }}
                sx={{ minWidth: 220 }}
              />
            </VuiBox>
            {loading ? (
              <VuiBox display="flex" justifyContent="center" alignItems="center" p={4}>
                <VuiTypography variant="button" color="white">
                  {t('common.loading')}
                </VuiTypography>
              </VuiBox>
            ) : clients.length === 0 ? (
              <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={5}>
                <img src={notFoundImg} alt={t('common.not_found')} style={{ maxWidth: 180, opacity: 0.7, marginBottom: 16 }} />
                <VuiTypography variant="button" color="text">
                  {t('client_table.no_clients_found')}
                </VuiTypography>
              </VuiBox>
            ) : (
              <VuiBox
                sx={{
                  "& th": {
                    borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                      `${borderWidth[1]} solid ${grey[700]}`,
                    position: 'sticky',
                    top: 0,
                    background: '#191932',
                    zIndex: 2,
                  },
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                        `${borderWidth[1]} solid ${grey[700]}`,
                    },
                  },
                  width: '100%',
                  px: 3,
                }}
              >
                <Table columns={columns} rows={rows} TableContainerProps={{ sx: { maxHeight: '390px' } }} />
              </VuiBox>
            )}
          </Card>

          {/* Map Card */}
          <Card sx={{ flex: 1, height: 'fit-content', minWidth: { lg: '400px' } }}>
            <VuiBox p={2}>
              <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <VuiBox display="flex" alignItems="center" gap={2}>
                  <VuiTypography variant="h6" color="white" fontWeight="bold">
                    {t('client_table.locations')}
                  </VuiTypography>
                  {selectedClients.size > 0 && (
                    <VuiTypography 
                      variant="caption" 
                      color="text"
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { color: '#2196f3' }
                      }}
                      onClick={clearSelection}
                    >
                      {t('client_table.clear_selection', { count: selectedClients.size })}
                    </VuiTypography>
                  )}
                </VuiBox>
                <Icon
                  sx={{ cursor: "pointer", fontWeight: "bold" }}
                  fontSize="small"
                  onClick={() => setMapFullscreen(!mapFullscreen)}
                >
                  {mapFullscreen ? <FiX /> : <FiMaximize2 />}
                </Icon>
              </VuiBox>
              <VuiBox
                sx={{
                  height: mapFullscreen ? "calc(100vh - 200px)" : "400px",
                  position: "relative",
                  transition: "height 0.3s ease",
                }}
              >
                <MapContainer
                  center={[31.9522, 35.9231]} // Default to Jordan
                  zoom={8}
                  style={{ height: "100%", width: "100%" }}
                  ref={mapFullscreen ? fullscreenMapRef : mapRef}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  {clients.map((client) => (
                    <Marker
                      key={client.id}
                      position={[client.latitude || 31.9522, client.longitude || 35.9231]}
                      icon={createCarIcon(client.car_icon)}
                      eventHandlers={{
                        click: () => handleMarkerClick(client.id),
                      }}
                    >
                      <Popup>
                        <VuiBox>
                          <VuiTypography variant="button" color="text" fontWeight="bold">
                            {`${client.first_name} ${client.last_name}`}
                          </VuiTypography>
                          <VuiTypography variant="caption" color="text">
                            {client.phone}
                          </VuiTypography>
                        </VuiBox>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </VuiBox>
            </VuiBox>
          </Card>
        </VuiBox>
      </VuiBox>
      <Footer />
      <ClientDetailsModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setClientDetails(null);
        }}
        client={clientDetails}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      <ClientEditModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedClientForEdit(null);
        }}
        client={selectedClientForEdit}
        onSave={handleSaveClient}
      />
      {/* Fullscreen Map Dialog */}
      <Dialog
        open={mapFullscreen}
        onClose={() => setMapFullscreen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            background: '#191932',
            color: '#fff',
            borderRadius: '18px',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
            height: '90vh',
            maxHeight: '90vh',
            margin: '20px',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <VuiBox p={2}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <VuiTypography variant="h6" color="white" fontWeight="bold">
              {t('client_table.locations')}
            </VuiTypography>
            <Box
              sx={{
                cursor: 'pointer',
                color: '#fff',
                background: 'rgba(34,34,58,0.8)',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                '&:hover': {
                  background: '#e53935',
                  color: '#fff',
                },
              }}
              onClick={() => setMapFullscreen(false)}
              title="Close"
            >
              <FiX size={20} />
            </Box>
          </VuiBox>
        </VuiBox>
        <Box sx={{ flexGrow: 1 }}>
          <MapContainer
            center={[31.9522, 35.9231]} // Default to Jordan
            zoom={8}
            style={{ height: "100%", width: "100%" }}
            ref={fullscreenMapRef}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {clients.map((client) => (
              <Marker
                key={client.id}
                position={[client.latitude || 31.9522, client.longitude || 35.9231]}
                icon={createCarIcon(client.car_icon)}
                eventHandlers={{
                  click: () => handleMarkerClick(client.id)
                }}
              >
                <Popup>
                  <VuiBox>
                    <VuiTypography variant="button" color="text" fontWeight="bold">
                      {`${client.first_name} ${client.last_name}`}
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text">
                      {client.phone}
                    </VuiTypography>
                  </VuiBox>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Box>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            background: 'background.default',
            color: 'text.primary',
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Are you sure you want to delete this client? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Client; 